import type { ReactNode } from "react";

/**
 * Tiny stat tile shared across admin sub-pages (Overview, BigQuery Metrics).
 * Renders a small uppercase label above a large numeric value.
 */
export function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/**
 * Bordered card with a small heading — used for grouped admin content blocks.
 */
export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

/**
 * Simple key/value list, monospace keys, right-aligned values.
 * Used by the BigQuery Metrics tab; kept here so the same primitive is
 * available to future admin sub-pages.
 */
export function TopList({ rows }: { rows: { key: string; value: string }[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">No data.</p>;
  }
  return (
    <ul className="space-y-1 text-sm">
      {rows.map((r) => (
        <li key={r.key} className="flex justify-between">
          <span className="truncate font-mono">{r.key}</span>
          <span className="text-slate-600">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Format ``n`` bytes as a short human-readable string (B / KB / MB / GB / TB).
 */
export function fmtBytes(n: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
